// `/biblioteca/:id` — LER A HISTÓRIA, e esta rota não existia.
//
// ── O DEFEITO, ACHADO LENDO O DOSSIÊ E MEDIDO NO CÓDIGO ─────────────────────────────────────
//
// `T2.21`: *"quando eu clico na biblioteca, tem essa parte criada, primeiro quadrinho, e ele me
// leva para a primeira página de todo jeito."*
//
// Medido em 2026-09-12, e a causa é de uma linha: `biblioteca.tsx` navegava para
// `/biblioteca/${cap.id}` ao clicar num capítulo, e **essa rota não existia no `App.tsx`**. Sete
// rotas listadas ali, `/biblioteca` entre elas, e nada abaixo — e este roteador não tem rota
// curinga. Clicar num item do catálogo caía no vazio.
//
// Então a prateleira estava pronta, provada e ligada a um endereço que ninguém havia criado. É o
// mesmo tipo de coisa que o `T2.53` era do outro lado: a leitura existia e a escrita não.
//
// ── A DIVISÃO FICHA / PÁGINAS, E POR QUE ESTA TELA PRECISA DAS DUAS ─────────────────────────
//
// `firestore.rules` guarda a decisão, que veio de outro pedido: *"a biblioteca aparece visível
// para quem quiser saber o que tem, mas para ler só logado."* Então:
//
//   biblioteca/{id}              a FICHA — título, autor, turma, quantas páginas. PÚBLICA.
//   biblioteca/{id}/paginas/{n}  a HISTÓRIA. Exige conta — e, se o quadrinho for de uma
//                                instituição, exige vínculo ativo nela (T2.63).
//
// Esta tela lê as duas, e o que ela faz quando a segunda é negada é o ponto: em vez de tela em
// branco ou erro técnico, ela **mostra a ficha e explica** — porque a ficha é justamente o
// preview que o T2.63 pede, e quem chegou aqui sem conta merece saber que a história existe.

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Nav from "../../components/nav";
import Firebase from "../../api/firebase.class";
import { paginasLidas } from "../../publicar/imagens-em-documentos";
import { useGlobalState } from "../../global-state-context";
import { DocumentoHQ, Pagina, ROTULO_CATEGORIA } from "../../api/documento.types";
import { VisualizadorDeDocumento } from "../../motores";

import { paginasDaFicha, rotuloDePaginas } from "./contagem-de-paginas";
import { quandoNaoHaPagina } from "../../components/quando-nao-ha-pagina";

import "./ler-historia.scss";

// `nao-deu` nasceu do T8.12: antes havia um estado a menos, e por isso a falha de REDE caía no
// mesmo balde do endereço errado. São consertos diferentes — um se resolve tentando de novo, o
// outro não —, e a tela que junta os dois manda a pessoa desistir de algo que ia funcionar.
type Estado = "carregando" | "achei" | "nao-achei" | "sem-acesso" | "nao-deu";

function LerHistoria() {
  const { id } = useParams();
  const navigate = useNavigate();
  const globalState = useGlobalState();
  const logado = !!globalState?.appUser;

  const [ficha, setFicha] = useState<DocumentoHQ | null>(null);
  const [paginas, setPaginas] = useState<Pagina[]>([]);
  const [estado, setEstado] = useState<Estado>("carregando");

  useEffect(() => {
    if (!id) return;
    let vivo = true;

    (async () => {
      // A FICHA PRIMEIRO. Ela é pública, então esta leitura é a que quase sempre funciona — e é
      // ela que decide se a história existe, antes de tentar o que pode ser negado.
      // ── POR QUE ESTA LEITURA PRECISA DE `try` E A DE BAIXO JÁ TINHA ──────────────────
      //
      // T8.12, medido em produção em 25/09/2026: `/biblioteca/nao-existe-este-id-999` ficava em
      // *"Abrindo a história…"* para sempre — 5 amostras ao longo de 14 s, todas idênticas, 284
      // caracteres de corpo e 57 px de miolo. O 404 que existe trinta linhas abaixo nunca
      // aparecia.
      //
      // A causa não está nesta tela: está na regra, e é de uma linha.
      //
      //     allow read: if resource.data.publicacao.estado == 'publicado';
      //
      // Para documento INEXISTENTE, `resource` é nulo, `resource.data` estoura, e o Firestore
      // responde **permission-denied** em vez de devolver vazio. Então `getDocument` REJEITA, a
      // promessa deste efeito morre sem ninguém ouvindo, e nenhum `setEstado` roda. O estado
      // inicial fica na tela — e o estado inicial é a frase de espera.
      //
      // E o rascunho cai no mesmo lugar, o que é DE PROPÓSITO: a regra nega tanto o que não
      // existe quanto o que existe e não foi publicado. Distinguir os dois na tela vazaria a
      // existência de quadrinho não publicado para quem chutasse endereços — então "não
      // encontrei" é a resposta certa para os dois, e não uma desistência de investigar.
      let f: DocumentoHQ | null = null;
      try {
        f = (await Firebase.getDocument("biblioteca", id)) as DocumentoHQ | null;
      } catch (e) {
        if (!vivo) return;
        const codigo = (e as { code?: string })?.code || "";
        // `permission-denied` e `not-found` são a mesma resposta de produto. Qualquer outro
        // código é rede, cota ou configuração — e aí dizer "não encontrei" seria mentira.
        const ehAusencia = codigo === "permission-denied" || codigo === "not-found";
        if (!ehAusencia) console.warn("[biblioteca] não consegui ler a ficha", e);
        setEstado(ehAusencia ? "nao-achei" : "nao-deu");
        return;
      }
      if (!vivo) return;
      if (!f) {
        setEstado("nao-achei");
        return;
      }
      setFicha(f);

      // AS PÁGINAS, que podem ser negadas. `getCollection` sem filtro aqui é seguro porque a
      // regra desta subcoleção decide por documento, e não por consulta — diferente da listagem
      // da prateleira, em que a ausência de `where` derruba tudo.
      try {
        const bruto = (await Firebase.getCollection(`biblioteca/${id}/paginas`)) as unknown[];
        if (!vivo) return;

        // ── A COLEÇÃO TRAZ PÁGINA **E** IMAGEM, DESDE O T6.6 ──────────────────────────────
        //
        // Cada imagem de uma página publicada é um documento irmão dela nesta mesma coleção —
        // porque o Firestore recusa documento acima de 1.048.487 bytes e uma página com 5 ou 6
        // desenhos passa disso com folga. `paginasLidas` separa os dois e devolve os data URIs
        // para dentro das páginas.
        //
        // E ela vem ANTES do `sort`, não depois: o id de um documento de imagem é `p1-img-0`, e
        // `Number("p1-img-0")` é `NaN`. Ordenar antes de separar embaralharia as páginas.
        const ps = paginasLidas(bruto);

        // Ordem por id numérico: o publicar grava "1", "2", "3", e ordem alfabética poria "10"
        // antes de "2" — que é a coisa mais fácil de não notar com menos de dez páginas.
        setPaginas([...ps].sort((a, b) => Number(a.id) - Number(b.id)));
        setEstado("achei");
      } catch (e) {
        if (!vivo) return;
        console.warn("[biblioteca] não consegui ler as páginas", e);
        setEstado("sem-acesso");
      }
    })();

    return () => {
      vivo = false;
    };
  }, [id]);

  // O TETO DE ESPERA, e ele é o cinto de segurança do conserto acima.
  //
  // O `try` trata a promessa que REJEITA. Sobra a que nunca assenta — sem rede, com a chave do
  // Firebase ausente, com a aba suspensa no meio. Nesses casos nenhum `catch` roda e a frase de
  // espera fica na tela indefinidamente, que é exatamente o sintoma do T8.12. Oito segundos é o
  // mesmo teto que a prateleira usa desde o T2: errar rápido é mais gentil do que errar devagar.
  useEffect(() => {
    if (estado !== "carregando") return;
    const t = setTimeout(() => setEstado("nao-deu"), 8000);
    return () => clearTimeout(t);
  }, [estado]);

  // O TÍTULO DA ABA em toda tela, e não só na que achou a história. Aba dizendo "HQ-Lab" em cima
  // de um endereço que não existe é a mesma informação que a tela em branco dava.
  useEffect(() => {
    if (ficha?.titulo) document.title = `${ficha.titulo} · HQ-Lab`;
    else if (estado === "nao-achei") document.title = "História não encontrada · HQ-Lab";
    else if (estado === "nao-deu") document.title = "Não deu para abrir · HQ-Lab";
    else if (estado === "carregando") document.title = "Abrindo a história… · HQ-Lab";
    return () => {
      document.title = "HQ-Lab";
    };
  }, [ficha, estado]);

  if (estado === "carregando") {
    return (
      <div className="ler-container">
        <Nav />
        <main className="ler-conteudo">
          <p className="ler-aviso">Abrindo a história…</p>
        </main>
      </div>
    );
  }

  if (estado === "nao-achei" || !ficha) {
    return (
      <div className="ler-container">
        <Nav />
        <main className="ler-conteudo">
          <h1>Não encontrei essa história</h1>
          <p className="ler-aviso">
            Ela pode ter saído da biblioteca, ou o endereço pode estar errado.
          </p>
          <button type="button" className="ler-botao" onClick={() => navigate("/biblioteca")}>
            Voltar para a biblioteca
          </button>
        </main>
      </div>
    );
  }

  // NÃO É A MESMA TELA DO 404, e a diferença é a única coisa acionável: aqui tentar de novo
  // resolve. Oferecer "voltar para a biblioteca" a quem só perdeu a rede é tirar da pessoa a
  // ação que ia funcionar.
  if (estado === "nao-deu") {
    return (
      <div className="ler-container">
        <Nav />
        <main className="ler-conteudo">
          <h1>Não deu para abrir a história agora</h1>
          <p className="ler-aviso">
            A história pode estar aqui — o que falhou foi a busca dela. Tente de novo em instantes.
          </p>
          <button type="button" className="ler-botao" onClick={() => window.location.reload()}>
            Tentar de novo
          </button>{" "}
          <button type="button" className="ler-botao" onClick={() => navigate("/biblioteca")}>
            Voltar para a biblioteca
          </button>
        </main>
      </div>
    );
  }

  // T8.11 — a contagem sai de `contagem-de-paginas.ts`, a mesma peça que a prateleira e a home
  // usam. Aqui ela prefere `paginas.length` quando as páginas chegaram, porque nesse caso a
  // lista é o dado medido e o campo da ficha é só o resumo gravado no dia da publicação.
  const quantas = paginas.length || paginasDaFicha(ficha);
  const finalizado = !!ficha.publicacao?.finalizado;

  // T10.2 · O QUINTO MUNDO, que não tinha tela. A leitura deu certo e não veio página: o leitor
  // desenhava uma caixa vazia embaixo do cabeçalho, sem frase e sem saída. A decisão do que dizer
  // está em `quando-nao-ha-pagina.ts`, compartilhada com o leitor do acervo — porque o buraco era
  // o mesmo nos dois, e um `if` em cada um é a garantia de divergirem na próxima mudança.
  const semPagina =
    estado === "sem-acesso"
      ? null
      : quandoNaoHaPagina({
          quantasChegaram: paginas.length,
          quantasORegistroDiz: paginasDaFicha(ficha),
          onde: "biblioteca",
        });

  return (
    <div className="ler-container">
      <Nav />

      <main className="ler-conteudo">
        <header className="ler-cabecalho">
          <div>
            <h1>{ficha.titulo}</h1>
            <p className="ler-credito">
              {ficha.autor?.nome || "Autor não identificado"}
              {ficha.autor?.turma ? ` · ${ficha.autor.turma}` : ""}
            </p>
            <p className="ler-detalhe">
              {ficha.publicacao?.categoria ? ROTULO_CATEGORIA[ficha.publicacao.categoria] : "—"}
              {rotuloDePaginas(quantas) ? ` · ${rotuloDePaginas(quantas)}` : ""}
              {/* O ESTADO DO T2.60 APARECE AQUI, e não só no filtro da prateleira. Quem abriu a
                  história é justamente quem precisa saber se o que está lendo pode mudar amanhã —
                  "você pode abrir ele amanhã e o balão de fala ter mudado". */}
              {` · ${finalizado ? "trabalho encerrado" : "ainda em construção"}`}
            </p>
          </div>

          <button type="button" className="ler-botao" onClick={() => navigate("/biblioteca")}>
            Voltar para a biblioteca
          </button>
        </header>

        {/* ── QUANDO AS PÁGINAS SÃO NEGADAS ───────────────────────────────────────────────
            A ficha continua visível — ela É o preview (T2.63). O que muda é a explicação, e ela
            é diferente para os dois casos, porque os consertos são diferentes: sem conta, entre;
            com conta e sem vínculo, é material de uma escola específica e senha nenhuma resolve. */}
        {estado === "sem-acesso" ? (
          <div className="ler-fechado">
            {!logado ? (
              <>
                <p>
                  <strong>Esta história precisa de conta para ser lida.</strong> O catálogo é aberto
                  — o que está aqui é o que existe na plataforma —, e a leitura é de quem tem conta.
                </p>
                <button
                  type="button"
                  className="ler-botao"
                  onClick={() =>
                    navigate("/login", {
                      state: { voltarPara: `/biblioteca/${id}`, motivo: "sem-conta" },
                    })
                  }
                >
                  Entrar para ler
                </button>
              </>
            ) : (
              <p>
                <strong>Esta história é de uma instituição de ensino.</strong> Só quem é educador ou
                estudante dela pode ler. Se você é da escola, peça vínculo em{" "}
                <button type="button" className="ler-link" onClick={() => navigate("/minha-conta")}>
                  minha conta
                </button>
                .
              </p>
            )}
          </div>
        ) : semPagina ? (
          <div className="ler-fechado" role="status">
            <p>
              <strong>{semPagina.titulo}</strong> {semPagina.explicacao}
            </p>
            {semPagina.acao && (
              <button
                type="button"
                className="ler-botao"
                onClick={() =>
                  semPagina.tentarDeNovo
                    ? window.location.reload()
                    : navigate(semPagina.acao!.destino)
                }
              >
                {semPagina.acao.rotulo}
              </button>
            )}
          </div>
        ) : (
          <VisualizadorDeDocumento
            documento={{ ...ficha, paginas }}
            larguraDisponivel={900}
          />
        )}
      </main>
    </div>
  );
}

export default LerHistoria;
